import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { useClinic } from '../hooks/useClinic';
import { servicesService, ServiceCategory, MedicalService, getServiceImageUrl } from '../services/servicesService';
import { getDoctorPhotoUrl } from '../services/clinicService';
import { createAppointmentRequest } from '../services/requestsService';
import { DoctorDetailCard } from '../components/features/DoctorDetailCard';
import { doctorsService } from '../services/doctorsService';
import { BackButton } from '../components/common/BackButton';
import { PopupMultiSelect, PopupTrigger } from '../components/common/PopupMultiSelect';
import { SuccessPopup } from '../components/common/SuccessPopup';
import { getMedicalIcon } from '../utils/medicalIcons';
import { getNearestWorkingHours } from '../utils/workScheduleUtils';

// КАРТОЧКА УСЛУГИ - адаптивная
const ServiceCard: React.FC<{
  service: MedicalService;
  onDetails: () => void;
  onBook: () => void;
  isSubmitting: boolean;
}> = ({ service, onDetails }) => {
  return (
    <Box
      onClick={onDetails}
      sx={{
        width: '100%',
        height: {
          xs: '130px',
          sm: '145px',
          md: '160px',
          lg: '180px',
        },
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        padding: {
          xs: '12px',
          sm: '14px',
          md: '16px',
        },
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:active': { transform: 'scale(0.97)' },
      }}
    >
      {/* Иконка сверху слева */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Box
          sx={{
            width: {
              xs: 36,
              md: 40,
            },
            height: {
              xs: 36,
              md: 40,
            },
            borderRadius: '50%',
            backgroundColor: '#007AFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getMedicalIcon(service.name, 20)}
        </Box>
      </Box>

      {/* Название */}
      <Typography
        sx={{
          fontSize: {
            xs: '14px',
            sm: '15px',
            md: '16px',
            lg: '18px',
          },
          fontWeight: 600,
          color: '#212121',
          lineHeight: 1.1,
        }}
      >
        {service.name}
      </Typography>
    </Box>
  );
};

// ТИП ЭКРАНА
type Screen = 'list' | 'detail' | 'doctors' | 'doctor-detail';

// ОСНОВНОЙ КОМПОНЕНТ
export const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branches, clinic } = useClinic();
  const currentBranch = branches.find((b) => b.id === user?.branchId);


  // Состояния
  const [screen, setScreen] = useState<Screen>('list');
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<MedicalService[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [showCatsPopup, setShowCatsPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<MedicalService | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Попапы
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [appointmentSubmitting, setAppointmentSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; type: 'success' | 'error' }>({
    open: false,
    message: '',
    type: 'success',
  });
  const [doctors, setDoctors] = useState<any[]>([]);
  const [preparation, setPreparation] = useState<any[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<any | null>(null);
  const [fullDoctor, setFullDoctor] = useState<any | null>(null);
  const [doctorDetailLoading, setDoctorDetailLoading] = useState(false);

  // ЗАГРУЗКА КАТЕГОРИЙ
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const catRes = await servicesService.getCategories();
        const cats = catRes.categories || [];
        setCategories(cats);
        setSelectedCategories(new Set());
        setCategoriesLoaded(true);
      } catch (e) {
        console.error('Ошибка при загрузке категорий:', e);
        setSnackbar({ open: true, message: 'Ошибка загрузки категорий', type: 'error' });
        setCategoriesLoaded(true);
      }
    };

    loadCategories();
  }, []);

  // Для отслеживания изменений в useEffect (memozied)
  const selectedCategoriesArray = useMemo(() => Array.from(selectedCategories), [selectedCategories]);

  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);

        if (selectedCategories.size === 0) {
          const servRes = await servicesService.getAllServices();
          const svcs = servRes.services || [];
          setServices(svcs);
        } else {
          const allServices: { [key: string]: MedicalService } = {};

          for (const catId of selectedCategories) {
            try {
              const servRes = await servicesService.getServicesByCategory(catId);
              (servRes.services || []).forEach((s) => {
                allServices[s.id] = s;
              });
            } catch (e) {
              console.error(`Ошибка при загрузке услуг категории ${catId}:`, e);
            }
          }

          const svcs = Object.values(allServices);
          setServices(svcs);
        }
      } catch (e) {
        console.error('Ошибка при загрузке услуг:', e);
        setSnackbar({ open: true, message: 'Ошибка загрузки услуг', type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    if (!categoriesLoaded) return;
    loadServices();
  }, [selectedCategoriesArray, categoriesLoaded]);

  // TOGGLE КАТЕГОРИЯ
  const toggleCategory = (catId: string) => {
    const next = new Set(selectedCategories);
    if (next.has(catId)) {
      next.delete(catId);
    } else {
      next.add(catId);
    }
    setSelectedCategories(next);
  };

  // ВЫБРАТЬ ВСЕ (очистить выбор)
  const selectAllCategories = () => {
    setSelectedCategories(new Set());
  };

  // LABEL ДЛЯ КНОПКИ КАТЕГОРИЙ
  const catLabel = useMemo(() => {
    if (selectedCategories.size === 0) {
      return 'Все категории';
    }
    return `Выбрано: ${selectedCategories.size}`;
  }, [selectedCategories.size]);

  // ОТКРЫТИЕ ДЕТАЛЕЙ УСЛУГИ
  const handleServiceClick = async (service: MedicalService) => {
    setSelectedService(service);
    setDetailLoading(true);
    setScreen('detail');
    setDoctors([]);
    setPreparation([]);

    try {
      // Загружаем полные данные услуги (с изображением)
      try {
        const fullServiceRes = await servicesService.getService(service.id);
        setSelectedService(fullServiceRes.service);
      } catch (e) {
        console.error('Ошибка при загрузке полных данных услуги:', e);
      }

      // Загружаем врачей и подготовку
      try {
        setDoctorsLoading(true);
        const doctorsRes = await servicesService.getServiceDoctors(service.id);

        // Нормализуем specializations в нужный формат
        const normalizedDoctors = (doctorsRes.doctors || []).map((doc: any) => ({
          ...doc,
          specializations: Array.isArray(doc.specializations)
            ? doc.specializations.map((s: any) =>
                typeof s === 'string' ? { name: s } : s
              )
            : [],
        }));

        setDoctors(normalizedDoctors);
      } catch (e) {
        console.error('Ошибка при загрузке врачей:', e);
        setDoctors([]);
      } finally {
        setDoctorsLoading(false);
      }

      try {
        const prepRes = await servicesService.getServicePreparation(service.id);
        setPreparation(prepRes.guides || []);
      } catch (e) {
        console.error('Ошибка при загрузке подготовки:', e);
      }
    } catch (e) {
      console.error('Ошибка при загрузке деталей:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  // ЗАПИСЬ НА УСЛУГУ
  const handleAppointment = async () => {
    if (!selectedService) return;
    setAppointmentSubmitting(true);

    try {
      await createAppointmentRequest({
        serviceId: selectedService.id,
      });

      setShowAppointmentDialog(true);
    } catch (e) {
      console.error('Ошибка при записи:', e);
      setSnackbar({ open: true, message: 'Ошибка при записи на услугу', type: 'error' });
    } finally {
      setAppointmentSubmitting(false);
    }
  };

  // РЕНДЕР ЭКРАНА СПИСКА
  const renderListScreen = () => (
    <>
      <BackButton onClick={() => navigate(-1)} />

      {/* Унифицированный контейнер: заголовок, описание, фильтр */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '768px' } }}>
          <Typography sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, fontWeight: 600, color: '#000', mb: 1, lineHeight: 1.1 }}>
            Услуги
          </Typography>
          <Typography sx={{ fontSize: { xs: 14, sm: 15, md: 16 }, fontWeight: 500, color: 'rgba(33,33,33,0.7)', mb: 2, lineHeight: 1.2 }}>
            Для просмотра подробной информации и для записи нажмите на карточку
          </Typography>

          {/* Попап триггер */}
          <PopupTrigger label={catLabel} onClick={() => setShowCatsPopup(true)} />
        </Box>
      </Box>

      {/* Попап с категориями */}
      <PopupMultiSelect
        open={showCatsPopup}
        onClose={() => setShowCatsPopup(false)}
        items={categories}
        selectedIds={selectedCategories}
        onToggle={toggleCategory}
        onSelectAll={selectAllCategories}
        allLabel="Все категории"
      />

      {/* Карточки услуг */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : services.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: '#8E8E93', textAlign: 'center', py: 4 }}>
          Услуги не найдены
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: { xs: '6px', sm: '8px', md: '12px' },
            justifyContent: 'center',
          }}
        >
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              onDetails={() => handleServiceClick(service)}
              onBook={() => {}}
              isSubmitting={false}
            />
          ))}
        </Box>
      )}
    </>
  );

  // РЕНДЕР ЭКРАНА ДЕТАЛЕЙ
  const renderDetailScreen = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        px: 1,
      }}
    >
      <BackButton onClick={() => setScreen('list')} />

      {detailLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : selectedService ? (
        <>
          {/* Контент */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pb: 2,
            }}
          >
            {/* Фото услуги или иконка */}
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '480px', md: '600px' },
                aspectRatio: '359 / 202',
                borderRadius: '16px',
                backgroundColor: '#E8E8E8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                overflow: 'hidden',
              }}
            >
              {selectedService.image ? (
                <img
                  src={getServiceImageUrl(selectedService.image) || ''}
                  alt={selectedService.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#007AFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getMedicalIcon(selectedService.name, 24)}
                </Box>
              )}
            </Box>

            {/* Название услуги + описание в одной плитке */}
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '480px', md: '600px' },
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                px: { xs: 2, sm: 2.5, md: 3 },
                py: { xs: 2, sm: 2.5 },
                mb: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: '#212121', mb: 1, lineHeight: 1 }}>
                {selectedService.name}
              </Typography>
              {selectedService.description && (
                <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: '#212121', lineHeight: 1.2 }}>
                  {selectedService.description}
                </Typography>
              )}
            </Box>

            {/* Текстовой контент */}
            <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' } }}>
              {/* Филиалы */}
              {selectedService.branches && selectedService.branches.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 12, color: 'rgba(33,33,33,0.5)', mb: 0.5 }}>Филиалы</Typography>
                  <Typography sx={{ fontSize: 14, color: '#212121' }}>
                    {selectedService.branches.map((b) => b.name).join(', ')}
                  </Typography>
                </Box>
              )}

              {/* Подготовка к услуге */}
              {preparation.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#000', mb: 1 }}>
                    Подготовка
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '16px',
                      p: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  >
                    {preparation.map((guide, idx) => (
                      <Box key={idx} sx={{ mb: idx < preparation.length - 1 ? 1.5 : 0 }}>
                        <Typography sx={{ fontSize: 13, color: '#212121', lineHeight: 1.5 }}>
                          {guide.title && <strong>{guide.title}</strong>}
                          {guide.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Кнопки фиксированы к низу экрана */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 96,
              left: 0,
              right: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              px: { xs: '12px', sm: '16px', md: '24px' },
              pb: 1,
              backgroundColor: '#F4F5F7',
              zIndex: 10,
            }}
          >
            <Box sx={{ display: 'flex', gap: '5px', mb: 1, width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' } }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/before-after?service=${selectedService.id}`, { state: { service: selectedService } })}
                sx={{
                  flex: 1,
                  height: { xs: 48, sm: 54, md: 58 },
                  borderColor: '#007AFF',
                  color: '#007AFF',
                  borderRadius: '22px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: 14, sm: 15, md: 16 },
                }}
              >
                До/после
              </Button>
              <Button
                variant="outlined"
                onClick={() => setScreen('doctors')}
                sx={{
                  flex: 1,
                  height: { xs: 48, sm: 54, md: 58 },
                  borderColor: '#007AFF',
                  color: '#007AFF',
                  borderRadius: '22px',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: 14, sm: 15, md: 16 },
                }}
              >
                Врачи
              </Button>
            </Box>
            <Button
              variant="contained"
              onClick={() => handleAppointment()}
              disabled={appointmentSubmitting}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '480px', md: '600px' },
                height: { xs: 48, sm: 54, md: 58 },
                backgroundColor: '#007AFF',
                borderRadius: '22px',
                fontWeight: 600,
                fontSize: { xs: 14, sm: 15, md: 16 },
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              {appointmentSubmitting ? (
                <CircularProgress size={24} sx={{ color: '#FFF' }} />
              ) : (
                'Записаться'
              )}
            </Button>
          </Box>
        </>
      ) : null}
    </Box>
  );

  // РЕНДЕР ЭКРАНА ВРАЧЕЙ
  const renderDoctorsScreen = () => (
    <>
      <BackButton onClick={() => setScreen('detail')} />

      <Typography sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, fontWeight: 600, color: '#000', mb: 1, lineHeight: 1.1 }}>
        Врачи
      </Typography>

      {doctorsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : doctors && doctors.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 } }}>
          {doctors.map((doctor) => (
            <Box
              key={doctor.id}
              onClick={async () => {
                setSelectedDoctor(doctor);
                setDoctorDetailLoading(true);
                try {
                  const res = await doctorsService.getDoctor(doctor.id);
                  setFullDoctor(res.doctor);
                } catch (e) {
                  console.error('Ошибка загрузки деталей врача:', e);
                  // Если ошибка загрузки, используем что есть
                  setFullDoctor(doctor);
                }
                setDoctorDetailLoading(false);
                setScreen('doctor-detail');
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                p: { xs: 1.5, sm: 2 },
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:active': { transform: 'scale(0.98)' },
              }}
            >
              <Avatar
                src={getDoctorPhotoUrl(doctor.image) || undefined}
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  backgroundColor: '#007AFF',
                  color: '#fff',
                  fontSize: { xs: 16, sm: 18 },
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {doctor.name?.split(' ')[0]?.charAt(0)?.toUpperCase() || '?'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 600, color: '#000' }}>
                  {doctor.name}
                </Typography>
                {doctor.specializations && doctor.specializations.length > 0 && (
                  <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: '#8E8E93', mt: 0.5 }}>
                    {doctor.specializations.map((s: any) => typeof s === 'string' ? s : s.name).join(', ')}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: '#8E8E93', textAlign: 'center', py: 4 }}>
          Врачи не найдены
        </Typography>
      )}
    </>
  );

  // РЕНДЕР ЭКРАНА ДЕТАЛЕЙ ВРАЧА
  const renderDoctorDetailScreen = () => {
    // Если ещё загружаем полные данные врача, показываем загрузку
    if (doctorDetailLoading && !fullDoctor) {
      return (
        <>
          <BackButton onClick={() => {
            setSelectedDoctor(null);
            setFullDoctor(null);
            setScreen('doctors');
          }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </>
      );
    }

    return (
      <DoctorDetailCard
        doctor={selectedDoctor}
        fullDoctor={fullDoctor}
        isLoadingDetails={false}
        onBack={() => {
          setSelectedDoctor(null);
          setFullDoctor(null);
          setScreen('doctors');
        }}
        onBook={() => setShowAppointmentDialog(true)}
        isSubmitting={appointmentSubmitting}
        clinicName={clinic?.name || 'Клиника'}
        workSchedule={currentBranch?.workSchedule || null}
        showBranches={false}
      />
    );
  };


  // РЕНДЕР
  if (screen === 'doctor-detail') {
    return (
      <AppLayout title="Услуги">
        {renderDoctorDetailScreen()}
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Услуги">
      {screen === 'list' && <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pb: 8 }}>{renderListScreen()}</Box>}
      {screen === 'detail' && renderDetailScreen()}
      {screen === 'doctors' && <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pb: 8 }}>{renderDoctorsScreen()}</Box>}

      <SuccessPopup
        open={showAppointmentDialog}
        onClose={() => {
          setShowAppointmentDialog(false);
          setScreen('list');
        }}
        clinicName={clinic?.name || 'Клиника'}
        workSchedule={currentBranch?.workSchedule || null}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} sx={{ borderRadius: '12px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppLayout>
  );
};
